// Hiring Process Constants

export const DEFAULT_STEP_COLOR = '#3B82F6'
export const DEFAULT_STEP_TYPE = 'interview'
export const DEFAULT_STEP_ESTIMATED_DURATION = 1

/** The 7 categories available when a user adds a hiring step. */
export const USER_ADD_STEP_CATEGORIES = [
  'initial',
  'scanning',
  'screening',
  'interview',
  'hr_interview',
  'hired',
  'rejected',
] as const

export type UserAddStepCategory = (typeof USER_ADD_STEP_CATEGORIES)[number]

export const STEP_CATEGORY_ICONS: Record<UserAddStepCategory, string> = {
  initial: '📝',
  scanning: '🤖',
  screening: '🔍',
  interview: '💬',
  hr_interview: '🤝',
  hired: '✅',
  rejected: '❌',
}

export const STEP_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#6366F1',
  '#14B8A6', '#F43F5E', '#A855F7', '#0EA5E9', '#22C55E'
]

export const CANDIDATE_FILTER_STEP_CATEGORIES = [
  'initial',
  'scanning',
  'screening',
  'interview',
  'hr_interview',
  'hired',
  'rejected',
] as const

export type CandidateFilterStepCategory = (typeof CANDIDATE_FILTER_STEP_CATEGORIES)[number]

export const DEFAULT_ACTION_TYPE = 'email'
export const DEFAULT_TRIGGER_TYPE = 'automatic'
export const DEFAULT_DELAY_HOURS = 0

// Default configuration for email actions
export const EMAIL_TEMPLATE_FIELD_KEY = 'template_id'

export type StepCategoryOption = {
  value: UserAddStepCategory
  label: string
  icon: string
  description: string
}

/** Build the 7 category options for the add-step modal. */
export function getUserAddStepCategoryOptions(
  t: any
): StepCategoryOption[] {
  return USER_ADD_STEP_CATEGORIES.map((value) => ({
    value,
    label: t(`hiringProcess.stepTypes.${value}`),
    icon: STEP_CATEGORY_ICONS[value],
    description: t(`hiringProcess.stepDescriptions.${value}`),
  }))
}

/** Default display name for a category (used when the user leaves name blank). */
export function getCategoryDefaultName(
  stepType: string,
  t: any
): string {
  return t(`hiringProcess.stepTypes.${stepType}`, stepType)
}

/** True when the user provided a custom name or description (deletable, non-duplicate by type). */
export function isCustomNamedStep(name: string, description: string): boolean {
  return Boolean(name.trim() || description.trim())
}

/** After resolving defaults, detect whether the user intentionally customized the step. */
export function isResolvedStepCustom(
  step: { name: string; description?: string; step_type: string },
  t: any
): boolean {
  const defaultName = getCategoryDefaultName(step.step_type, t)
  return Boolean(
    (step.description || '').trim() ||
    step.name.trim().toLowerCase() !== defaultName.trim().toLowerCase()
  )
}

/** Resolve step payload before API submit: fill default name when blank. */
export function resolveNewStepFormData(
  form: {
    name: string
    description: string
    step_type: string
    color: string
    is_required: boolean
    estimated_duration: number
    is_active: boolean
  },
  t: any
) {
  const trimmedName = form.name.trim()
  const trimmedDescription = form.description.trim()
  const isCustom = isCustomNamedStep(trimmedName, trimmedDescription)
  const defaultName = getCategoryDefaultName(form.step_type, t)

  return {
    ...form,
    name: trimmedName || defaultName,
    description: trimmedDescription,
    is_required: isCustom ? false : form.is_required,
    isCustom,
  }
}

/** Duplicate check for new steps: custom names must be unique; default-category steps may repeat. */
export function isDuplicateNewStep(
  existingSteps: Array<{ name?: string; step_type?: string }>,
  stepType: string,
  name: string,
  isCustom: boolean
): boolean {
  const normalizedName = name.trim().toLowerCase()
  if (isCustom) {
    return existingSteps.some(
      (s) => (s.name || '').trim().toLowerCase() === normalizedName
    )
  }
  return false
}

/** Position options for inserting a new step (after core steps, before rejected). */
export function buildStepPositionOptions(
  sortedSteps: Array<{ id?: number; order?: number; name?: string; step_type?: string }>,
  t: any,
  options?: { useDirectOrder?: boolean }
): { positionOptions: Array<{ value: number; label: string }>; defaultPosition: number } {
  const stepsForPosition = sortedSteps.filter(
    (s) => (s.order || 0) >= 3 && s.step_type !== 'rejected'
  )

  const positionOptions: Array<{ value: number; label: string }> = stepsForPosition.map((s) => {
    const value = options?.useDirectOrder
      ? (s.order || 0) + 1
      : sortedSteps.findIndex((step) => step.id === s.id) + 2
    return {
      value,
      label: t('hiringProcess.afterStep', { step: s.name || '' }),
    }
  })

  const maxOrder = sortedSteps.length
    ? Math.max(...sortedSteps.map((s) => s.order || 0))
    : 0
  const endValue = options?.useDirectOrder ? maxOrder + 1 : sortedSteps.length + 1

  positionOptions.push({
    value: endValue,
    label: t('hiringProcess.atEnd', 'At the end'),
  })

  return {
    positionOptions,
    defaultPosition: positionOptions[0]?.value ?? endValue,
  }
}
